import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Copy, Users, TrendingUp, GraduationCap, Clock, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import Header from "@/components/Header";
import { Card } from "@/components/ui/card";

const ROLE_LABEL = { admin: "Admin", affiliate_manager: "Leader", participant: "Member" };

function StatCard({ icon: Ic, label, value, color }) {
  return (
    <Card className="p-4 flex items-center gap-3">
      <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: `${color}1A`, color }}>
        <Ic size={16} />
      </span>
      <div>
        <div className="text-xl font-bold num leading-none">{value}</div>
        <div className="text-[11px] text-ink-3 mt-1">{label}</div>
      </div>
    </Card>
  );
}

export default function MyTeam() {
  const [team, setTeam] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [t, s] = await Promise.all([
          api.get("/leader/team").then((r) => r.data.team || []),
          api.get("/leader/funnel-stats").then((r) => r.data),
        ]);
        setTeam(t); setStats(s);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load your team");
      } finally { setLoading(false); }
    })();
  }, []);

  const funnelUrl = stats?.funnel_path ? `${window.location.origin}${stats.funnel_path}` : "";

  return (
    <div className="min-h-screen pb-16">
      <Header />
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 pt-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="text-2xl font-bold" data-testid="my-team-title">My Team</h1>
            <p className="text-sm text-ink-3 mt-1">Read-only view of everyone who joined through you and your people.</p>
          </div>
          {funnelUrl && (
            <button
              onClick={() => { navigator.clipboard.writeText(funnelUrl); toast.success("Funnel link copied"); }}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium"
              style={{ background: "rgba(64,96,255,0.10)", color: "#4060FF" }}
              data-testid="copy-funnel-link-btn"
            >
              <Copy size={13} /> Copy my funnel link
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-16"><Loader2 className="animate-spin mx-auto" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8" data-testid="funnel-stats-cards">
              <StatCard icon={Users} label="Signups" value={stats?.signups ?? 0} color="#4060FF" />
              <StatCard icon={GraduationCap} label="In training" value={stats?.in_progress ?? 0} color="#7C3AED" />
              <StatCard icon={Clock} label="Awaiting approval" value={stats?.awaiting ?? 0} color="#B45309" />
              <StatCard icon={CheckCircle2} label="Members" value={stats?.members ?? 0} color="#00863A" />
              <StatCard icon={TrendingUp} label="Conversion" value={`${stats?.conversion ?? 0}%`} color="#EC4899" />
            </div>

            <Card className="p-0 overflow-x-auto">
              <table className="modern members-compact" data-testid="my-team-table">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Wallet / tile</th>
                    <th>Joined</th>
                    <th>Last login</th>
                  </tr>
                </thead>
                <tbody>
                  {team.length === 0 ? (
                    <tr><td colSpan="6" className="text-center py-10 text-ink-3" data-testid="my-team-empty">
                      No one has joined under you yet — share your funnel link to get started.
                    </td></tr>
                  ) : team.map((m) => (
                    <tr key={m.id} data-testid={`team-row-${m.id}`}>
                      <td>
                        <div className="font-medium text-xs">{m.name || "—"}</div>
                        <div className="text-[11px] text-ink-3 font-mono">{m.email}</div>
                      </td>
                      <td>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                              style={{ background: "rgba(64,96,255,0.10)", color: "#4060FF" }}>
                          {ROLE_LABEL[m.role] || m.role}
                        </span>
                      </td>
                      <td>
                        <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${m.status === "Active" ? "text-emerald-700" : "text-ink-3"}`}>
                          {m.status === "Active" ? <CheckCircle2 size={11} /> : <XCircle size={11} />} {m.status || "—"}
                        </span>
                      </td>
                      <td className="text-xs">{m.node_label || <span className="text-ink-3">—</span>}</td>
                      <td className="text-[11px] text-ink-3 font-mono">{m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}</td>
                      <td className="text-[11px] text-ink-3 font-mono">{m.last_login_at ? new Date(m.last_login_at).toLocaleDateString() : "Never"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
